import type { PropsWithChildren, ReactElement } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import type { ApiContext, ApiItemType } from '@highimpact-frontend/api-types';
import {
  createDashboardHostApi,
  DashboardHostProvider,
  DashboardLibraryProvider,
  type DashboardGridType,
  type DashboardHostContextValue,
  type DashboardHostMode,
  type DashboardHttpTransport,
  type RemoteWidgetRenderRequest,
} from '@highimpact/dashboard';
import {
  api_config,
  api_data_dasboard,
  api_permissions,
  api_report,
  api_semantic,
} from '../api/api-commons';
import DashboardService from '../api/services/config/dashboard/DashboardService';
import CampaignService from '../api/services/config/campaign/CampaignService';
import MfeService from '../api/services/config/mfe/MfeService';
import ReportModelService from '../api/services/config/reportModel/ReportModelService';
import dashboardElementResourceService from '../api/services/config/resource/DasboardElementResourceService';
import resourceService from '../api/services/config/resource/ResourceService';
import SourceLabelService from '../api/services/config/sources/SourceLabelService';
import TagService from '../api/services/config/tags/TagService';
import RemoteLoader from '../components/RemoteLoader';
import { showSnackbar } from '../store/components/snackbarStore';
import hostStore from '../store/store';
import { loadAndExecuteRemoteFunction } from '../utils/executeRemoteFunctions';

export interface DashboardHostRouteShellProps extends PropsWithChildren {
  readonly project_id: number | null;
  readonly site_id: number | null;
  readonly grid_id: number | null;
  readonly mode: DashboardHostMode;
  readonly grid_type?: DashboardGridType;
  readonly simplified_view?: boolean;
  readonly is_editable?: boolean;
  readonly dashboard_only_mode?: boolean;
  readonly buildDashboardUrl?: (gridId: number) => string;
  readonly buildReportUrl?: (gridId: number) => string;
  readonly checkPermission?: (
    item: ApiItemType,
    extra?: Record<string, unknown>
  ) => boolean;
  readonly enableReportFeatures?: boolean;
}

const defaultCheckPermission = (
  item: ApiItemType,
  _extra?: Record<string, unknown>
): boolean => {
  const permissions =
    hostStore.getState().data_slice?.credentials?.permissions ?? {};
  const permissionEntry = permissions[item as keyof typeof permissions];

  if (
    permissionEntry === undefined ||
    typeof permissionEntry !== 'object' ||
    !('data' in permissionEntry)
  ) {
    return true;
  }

  const actions = permissionEntry.data as Record<string, boolean>;
  return Boolean(actions.read ?? actions.update ?? actions.create);
};

function buildHttpTransport(): DashboardHttpTransport {
  return {
    config: api_config,
    dataDashboard: api_data_dasboard,
    permissions: api_permissions,
    semantic: api_semantic,
    getAuthToken: () => sessionStorage.getItem('token'),
  };
}

/** Host-owned route shell: wires services + library store for dashboard pages. */
export function DashboardHostRouteShell({
  children,
  project_id,
  site_id,
  grid_id,
  mode,
  grid_type = 'grids',
  simplified_view = false,
  is_editable = true,
  dashboard_only_mode = false,
  buildDashboardUrl,
  buildReportUrl,
  checkPermission = defaultCheckPermission,
  enableReportFeatures = true,
}: DashboardHostRouteShellProps): ReactElement {
  const navigate = useNavigate();

  const transport = useMemo(() => buildHttpTransport(), []);

  const hostContext = useMemo((): DashboardHostContextValue => {
    const api = createDashboardHostApi({
      dashboard: DashboardService,
      mfe: MfeService,
      resource: resourceService,
      site: {
        getAll: () =>
          api_config.get('/sites').then((response) => response.data),
      },
      project: {
        getAll: () =>
          api_config.get('/projects').then((response) => response.data),
      },
      tag: TagService,
      sources: SourceLabelService,
      dashboardElementResource: dashboardElementResourceService,
      reportModel: ReportModelService,
      campaign: {
        getBySite: (siteId) =>
          CampaignService.getByEntity({ context: 'site', id: siteId }),
      },
      measures: {
        query: (body, signal) =>
          api_data_dasboard
            .post('/measures/v2', body, { signal })
            .then((response) => response.data),
      },
      measureTypes: {
        getAll: () =>
          api_config.get('/measure_types').then((response) => {
            const data = response.data;
            if (Array.isArray(data)) {
              return data;
            }
            return Object.values(data ?? {});
          }),
      },
      permissions: {
        fetchFrontendPermission: async (
          params: ApiContext,
          signal: AbortSignal
        ) => {
          const response = await api_permissions.post(
            '/frontend-permissions',
            params,
            { signal }
          );
          if (!response.data) {
            throw new Error('Failed to fetch permissions');
          }
          return response.data;
        },
      },
      buildReportDocx: (reportStructure) =>
        api_report
          .post<Blob>('/build_report', reportStructure, {
            responseType: 'blob',
          })
          .then((response) => response.data),
    });

    const report = enableReportFeatures ? { enabled: true as const } : undefined;

    return {
      project_id,
      site_id,
      grid_id,
      mode,
      grid_type,
      simplified_view,
      is_editable,
      dashboard_only_mode,
      navigate,
      getAuthToken: () => sessionStorage.getItem('token'),
      checkPermission,
      buildDashboardUrl:
        buildDashboardUrl ??
        ((id) =>
          project_id && site_id
            ? `/projects/${project_id}/sites/${site_id}/tableau-de-bord/${id}`
            : `/home/dashboards/${id}`),
      buildReportUrl:
        buildReportUrl ??
        ((id) =>
          project_id && site_id
            ? `/projects/${project_id}/sites/${site_id}/rapports/${id}`
            : `/home/dashboards/${id}`),
      api,
      showNotification: (message, severity) => showSnackbar(message, severity),
      report,
      federationHost: {
        isReady: () =>
          Boolean(
            (globalThis as { singleton_federation_host?: unknown })
              .singleton_federation_host
          ),
        registerRemote: () => undefined,
        executeRemoteFunction: (options) =>
          loadAndExecuteRemoteFunction({
            ...options,
            args: options.args ? [...options.args] : undefined,
          }),
      },
      renderRemoteWidget: ({
        remoteName,
        remoteUrl,
        moduleName,
        props,
      }: RemoteWidgetRenderRequest) => (
        <RemoteLoader
          remoteName={remoteName}
          remoteUrl={remoteUrl}
          moduleName={moduleName}
          props={props}
        />
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    buildDashboardUrl,
    buildReportUrl,
    checkPermission,
    dashboard_only_mode,
    enableReportFeatures,
    grid_id,
    grid_type,
    is_editable,
    mode,
    project_id,
    simplified_view,
    site_id,
  ]);

  return (
    <DashboardHostProvider value={hostContext}>
      <DashboardLibraryProvider transport={transport} hostApi={hostContext.api}>
        {children}
      </DashboardLibraryProvider>
    </DashboardHostProvider>
  );
}
